import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { SelectBox } from '@tecuity/granite-ui'
import { ContextBar, ContextButton } from '@tecuity/granite-ui'
import { Body } from '../Shell/Shell'
import { GraniteDataGrid } from '@tecuity/granite-ui/GraniteDataGrid'
import { SearchBox } from '@tecuity/granite-ui/SearchBox'
import {
  SET_PRINTQUEUE_USERS,
  SET_PRINTQUEUE_TYPES,
  SET_PRINTQUEUE_RETURN_METHOD,
  SET_PRINTQUEUE_DATA
} from './PrintQueueReducer'
import * as api from '../../requests'
import FileDownloader from 'components/ImageViewer/FileDownloader'
import '../WorkviewManager/WorkviewManager.scss'
import { useHistory } from 'react-router'
import { setErrorToast, setSuccessToast } from '../../actions'
import { Spinner } from '../Loaders/Loaders'
import format from 'date-fns/format'
import { useDebounce } from '../../Utilities/hooks'
import Portal from 'components/Portal/Portal'
import { ConfirmDialog } from 'components/Modal/Modal'
import { noop } from 'utilities'

// PrintQueue - PrintQueueDeleteEntries
// PrintQueue - PrintQueueDeleteEntriesAllUsers
// PrintQueue - PrintQueuePrintEntries
// PrintQueue - PrintQueueViewAllUsers
// PrintQueue - PrintQueueViewEntries
// PrintQueue - PrintQueueViewSystemUser

const PrintQueue = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const { users, types, returnMethods, printQueueData, printQueueTotalCount } = useSelector(
    (state) => state.printQueue,
    shallowEqual
  )
  const { appUserRights, UserId, FirstLastName } = useSelector((state) => state.auth, shallowEqual)
  const { sidebarCollapsed } = useSelector((state) => state.app, shallowEqual)

  const [selectedUser, setSelectedUser] = useState(
    appUserRights.includes('PrintQueueViewAllUsers') ? 0 : UserId
  )
  const [selectedType, setSelectedType] = useState(0)
  const [selectedMethod, setSelectedMethod] = useState(0)
  const [selectedRows, setSelectedRows] = useState([])
  const [pageSize, setPageSize] = useState(25)
  const [pageNumber, setPageNumber] = useState(0)
  const [searchText, setSearchText] = useState('')
  const debouncedSearchText = useDebounce(searchText, 250)
  const [sortColumn, setSortColumn] = useState('')
  const [sortByDesc, setSortByDesc] = useState('')
  const [loadingDropdowns, setLoadingDropdowns] = useState(false)
  const [loadingPrintQueue, setLoadingPrintQueue] = useState(false)
  const [loadingContextBar, setLoadingContextBar] = useState(false)
  const [confirmDeleteOpened, setConfirmDeleteOpened] = useState(false)
  const downloader = useRef()
  const [downloadOnNextTick, setDownloadOnNextTick] = useState(false)
  const [zipBlob, setZipBlob] = useState(null)
  const [blob, setBlob] = useState(null)

  useEffect(() => {
    if (!appUserRights.includes('PrintQueueViewEntries')) {
      dispatch(setErrorToast('User does not have rights to view Print Queue.'))
      history.push('/')
    } else {
      const promises = []
      if (!appUserRights.includes('PrintQueueViewAllUsers')) {
        dispatch({
          type: SET_PRINTQUEUE_USERS,
          users: [{ label: FirstLastName, value: UserId }]
        })
      } else {
        promises.push(
          new Promise((res, rej) => {
            api.GET_PRINT_QUEUE_USERS((users) => {
              // user has PrintQueueViewAllUsers right
              users.unshift({ FullName: 'All', UserId: '0' })

              dispatch({
                type: SET_PRINTQUEUE_USERS,
                users: users
                  .filter((user) => {
                    if (!appUserRights.includes('PrintQueueViewSystemUser') && user.UserId === 1)
                      return false
                    if (user.UserId === -1) return false // unassigned user
                    return true
                  })
                  .map((user) => {
                    return { label: user.FullName, value: user.UserId }
                  })
              })
              res()
            }, rej)
          })
        )
      }
      promises.push(
        new Promise((res, rej) => {
          api.GET_ALL_ENUM_LOOKUP_TYPE(
            'PrintQueueEntryType',
            (data) => {
              // console.log(data)
              data.unshift({ Name: 'All', Id: 0 })
              dispatch({
                type: SET_PRINTQUEUE_TYPES,
                types: data.map((type) => {
                  return { label: type.Name, value: type.Id }
                })
              })
              res()
            },
            rej
          )
        })
      )
      promises.push(
        new Promise((res, rej) => {
          api.GET_ALL_ENUM_LOOKUP_TYPE(
            'MethodOfReturnType',
            (data) => {
              // console.log(data)
              data.unshift({ Name: 'All', Id: 0 })
              dispatch({
                type: SET_PRINTQUEUE_RETURN_METHOD,
                returnMethods: data.map((type) => {
                  return { label: type.Name, value: type.Id }
                })
              })
              res()
            },
            rej
          )
        })
      )
      setLoadingDropdowns(true)
      Promise.all(promises)
        .then(() => {
          setLoadingDropdowns(false)
        })
        .catch((err) => {
          setLoadingDropdowns(false)
          console.error(err)
        })
    }
  }, [])

  const getPrintQueueData = () => {
    setLoadingPrintQueue(true)
    api.GET_PRINT_QUEUE_DATA(
      pageSize,
      selectedUser,
      selectedType,
      selectedMethod,
      pageNumber,
      sortColumn,
      sortByDesc,
      debouncedSearchText,
      (data) => {
        setLoadingPrintQueue(false)
        dispatch({
          type: SET_PRINTQUEUE_DATA,
          printQueueData: data.Entries || [],
          totalCount: data.TotalCount || 0
        })
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to load Print Queue'))
        setLoadingPrintQueue(false)
      }
    )
  }

  useEffect(() => {
    getPrintQueueData()
  }, [
    selectedUser,
    selectedType,
    selectedMethod,
    pageNumber,
    pageSize,
    debouncedSearchText,
    sortColumn,
    sortByDesc
  ])

  const onSortModelChange = (sortModel) => {
    dispatch(setSuccessToast('Re-Sorting - Returning to the first page'))
    const sortCol = sortModel.length > 0 ? sortModel[0] : {}

    if (sortCol.field) {
      let sortBy = ''
      switch (sortCol.field) {
        case 'UserName':
          sortBy = 'userName'
          break
        case 'PrintQueueTypeName':
          sortBy = 'printQueueType'
          break
        case 'Description':
          sortBy = 'description'
          break
        case 'EntryDate':
          sortBy = 'entryDate'
          break
        case 'Status':
          sortBy = 'status'
          break
        case 'MethodOfReturnTypeId':
          sortBy = 'methodOfReturnType'
          break
        default:
          sortBy = ''
          break
      }
      setSortColumn(sortBy)
      setSortByDesc(sortCol.sort === 'asc' ? false : true)
    } else {
    }
    setPageNumber(0)
  }

  const tableColumns = useMemo(() => {
    return [
      {
        field: 'UserName',
        flex: 0.1,
        minWidth: 20,
        headerName: 'User',
        disableColumnMenu: true,
        renderCell: (params) => {
          const user = users.find((user) => +user.value === +params.row.AppUserId)
          return user ? user.label : params.row.AppUserId
        }
      },
      {
        field: 'PrintQueueEntryTypeId',
        flex: 0.1,
        minWidth: 20,
        headerName: 'Type',
        disableColumnMenu: true
      },
      {
        field: 'FileType',
        flex: 0.1,
        minWidth: 20,
        headerName: 'File Type',
        disableColumnMenu: true
      },
      {
        field: 'Description',
        flex: 0.3,
        minWidth: 20,
        headerName: 'Description',
        disableColumnMenu: true
      },
      {
        field: 'EntryDate',
        flex: 0.15,
        minWidth: 20,
        headerName: 'Date',
        disableColumnMenu: true,
        renderCell: (params) => {
          return format(new Date(params.row.EntryDate), 'MM/dd/yyyy h:mm a')
        }
      },
      { field: 'Status', flex: 0.1, minWidth: 20, headerName: 'Status', disableColumnMenu: true },
      {
        field: 'MethodOfReturnTypeId',
        flex: 0.1,
        minWidth: 20,
        headerName: 'Method of Return',
        disableColumnMenu: true
      }
    ]
  }, [returnMethods, types, users])

  useEffect(() => {
    if (zipBlob) {
      setDownloadOnNextTick(true)
    }
  }, [zipBlob])

  useEffect(() => {
    if (downloadOnNextTick) {
      downloader.current.download()
      setDownloadOnNextTick(false)
    }
  }, [downloadOnNextTick])

  const exportToPdf = () => {
    // zip file of multiple pdfs
    api.PRINT_QUEUE_ENTRIES(
      selectedRows,
      (data) => {
        if (data.ok) {
          data.blob().then((blob) => {
            const blobObject = window.URL.createObjectURL(blob)
            setBlob(blobObject)
            setZipBlob(blob)
            setDownloadOnNextTick(true)
          })
        } else {
          console.error('Did this break')
        }
      },
      (err) => {
        console.error(err)
      }
    )
  }

  const printSelectedRows = () => {
    // single pdf of all selected rows
    setLoadingContextBar(true)
    api.EXPORT_PRINT_QUEUE_ITEMS_TO_PDF(
      selectedRows,
      (data) => {
        // Transfer base64 to Pdf
        var binaryString = atob(data.Pdf)
        var byteNumbers = new Array(binaryString.length)
        for (var i = 0; i < binaryString.length; i++) {
          byteNumbers[i] = binaryString.charCodeAt(i)
        }
        var byteArray = new Uint8Array(byteNumbers)
        // Make File with Bytes
        var file = new Blob([byteArray], { type: 'application/pdf;base64' })
        // Make Object to Open
        var fileUrl = URL.createObjectURL(file)
        const tab = window.open(fileUrl)
        tab.print()
        dispatch(setSuccessToast('PDF prepared to print'))
        setLoadingContextBar(false)
        getPrintQueueData()
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to prepare PDF to print'))
        setLoadingContextBar(false)
      }
    )
  }

  const markAsFulfilled = () => {
    // mark selected rows as fulfilled
    setLoadingContextBar(true)
    api.PRINT_QUEUE_MARK_AS_FULFILLED(
      selectedRows,
      (data) => {
        dispatch(setSuccessToast('Marked as Fulfilled'))
        setLoadingContextBar(false)
        getPrintQueueData()
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast('Failed to Mark as Fulfilled'))
        setLoadingContextBar(false)
      }
    )
  }

  const hasRightsToDeleteSelected = useMemo(() => {
    // If the AppUserRight PrintQueueDeleteEntriesAllUsers is NOT included then make sure all selected rows marked for deletion are created by User
    if (!appUserRights.includes('PrintQueueDeleteEntriesAllUsers')) {
      let selectedEntries = printQueueData.filter(x => selectedRows.includes(x.PrintQueueEntryId));
      return selectedEntries.every((element) => element.AppUserId == UserId)
    }      
    return true;
  }, [selectedRows])

  const confirmDeleteMessage = useMemo(() => {
    return `Are you sure you want to delete ${selectedRows.length} print queue item(s)?`
  }, [selectedRows])

  const deleteSelectedRows = () => {
    // delete selected rows
    setLoadingContextBar(true)
    api.DELETE_PRINT_QUEUE_ENTRIES(
      selectedRows,
      (data) => {
        // console.log(data)
        dispatch(setSuccessToast(data))
        setLoadingContextBar(false)
        getPrintQueueData()
      },
      (err) => {
        console.error(err)
        dispatch(setErrorToast(err))
        setLoadingContextBar(false)
      }
    )
  }

  return (
    <Body className="workview-manager">
      <div className="wvm-wrapper">
        <div className="actions-row">
          <div className="left-aligned dropdown">
            <SelectBox
              label="User"
              options={users}
              value={selectedUser}
              onChange={(e) => setSelectedUser(e)}
              noPlaceholder
            />
            <SelectBox
              label="Queue Type"
              options={types}
              value={selectedType}
              onChange={(e) => setSelectedType(e)}
              noPlaceholder
            />
            <SelectBox
              label="Method of Return"
              options={returnMethods}
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e)}
              noPlaceholder
            />
            {loadingDropdowns && (
              <div style={{ paddingLeft: '10px', paddingTop: '10px' }}>
                <Spinner size="small" themeColor="dark-neutral" />
              </div>
            )}
          </div>
          <div className="wvm-search-wrapper">
            <SearchBox
              placeholder={`Search...`}
              value={searchText}
              onChange={(e) => setSearchText(e)}
              onClear={() => setSearchText('')}
              showClearButton={true}
              showSearchButton={false}
            />
          </div>
        </div>
        <div className="table-wrapper">
          <GraniteDataGrid
            loading={loadingPrintQueue}
            getRowId={(row) => row.PrintQueueEntryId}
            columns={tableColumns}
            rows={printQueueData}
            rowCount={printQueueTotalCount}
            paginationMode="server"
            page={pageNumber}
            onPageChange={(newPage) => setPageNumber(newPage)}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            rowsPerPageOptions={[25, 50, 100]}
            checkboxSelection
            selectionModel={selectedRows}
            onSelectionModelChange={(ids) => {
              if(printQueueData.filter(x => ids.includes(x.PrintQueueEntryId)).length === printQueueData.length) {
                let zips = printQueueData.filter(x => ids.includes(x.PrintQueueEntryId) && x.PrintQueueFileTypeCode == 1 && x.PrintQueueFileTypeCode == 1);
                zips.forEach(zipFile => { ids.splice(ids.indexOf(zipFile.PrintQueueEntryId), 1) });

                dispatch(setSuccessToast('Zip files have been deselected as they are large files, and must be dealt with individually.'))
                setSelectedRows(ids);
                return;
                }

              let newFile = printQueueData.find(y => y.PrintQueueEntryId == ids.find(x => !selectedRows.includes(x)));
              if(newFile && selectedRows.length > 0 && newFile.PrintQueueFileTypeCode == 1){
                dispatch(setErrorToast('You must print/process zip files individually due to their size.'))
                return;
                }

              let zipFiles = printQueueData.filter(x => selectedRows.includes(x.PrintQueueEntryId) && ids.includes(x.PrintQueueEntryId) && x.PrintQueueFileTypeCode == 1);
              for(let i = 0; i < zipFiles.length; i++) {
                ids.splice(ids.indexOf(zipFiles[i].PrintQueueEntryId), 1);
                dispatch(setErrorToast('You must print/process zip files individually due to their size.'))
                }
              setSelectedRows(ids);
              }}
            disableVirtualization
            sortingMode="server"
            onSortModelChange={onSortModelChange}
          />
        </div>
      </div>

      <div className={`context-bar-wrapper ${sidebarCollapsed ? 'sidebar-collapsed ' : ''}`}>
        <ContextBar className="granite-context-bar">
          {appUserRights.includes('PrintQueuePrintEntries') && (
            <ContextButton
              disabled={selectedRows.length === 0 || loadingContextBar}
              variant="secondary"
              onClick={exportToPdf}
            >
              Export to Zip
            </ContextButton>
          )}
          {appUserRights.includes('PrintQueuePrintEntries') && (
            <ContextButton
              disabled={selectedRows.length === 0 || loadingContextBar}
              variant="secondary"
              onClick={printSelectedRows}
            >
              Print
            </ContextButton>
          )}
          {appUserRights.includes('PrintQueuePrintEntries') && (
            <ContextButton
              disabled={selectedRows.length === 0 || loadingContextBar}
              variant="secondary"
              onClick={markAsFulfilled}
            >
              Mark as Fulfilled
            </ContextButton>
          )}
          {appUserRights.includes('PrintQueueDeleteEntries') && (
            <ContextButton
              disabled={selectedRows.length === 0 || loadingContextBar || !hasRightsToDeleteSelected}
              variant="secondary"
              onClick={() => setConfirmDeleteOpened(true)}
            >
              Delete
            </ContextButton>
          )}
          {loadingContextBar && (
            <div style={{ paddingLeft: '5px', paddingRight: '15px', marginTop: '-3px' }}>
              <Spinner size="small" themeColor="dark-neutral" />
            </div>
          )}
        </ContextBar>
      </div>
      <Portal isOpened={confirmDeleteOpened}>
        <ConfirmDialog
          closeModal={() => {
            setConfirmDeleteOpened(false)
          }}
          onModalClosed={noop}
          message={confirmDeleteMessage}
          buttons={[
            { label: 'No', onClick: noop, className: 'btn-default' },
            {
              label: 'Yes',
              onClick: deleteSelectedRows,
              className: 'btn-danger'
            }
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
      <FileDownloader
        ref={downloader}
        href={blob}
        blob={zipBlob}
        fileName={`print-queue-${format(new Date(), 'MM-dd-yyyy')}.zip`}
      />
    </Body>
  )
}

export default PrintQueue
